import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";

import {
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FloatingLabelTextarea,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components";
import { useI18n } from "@/context/i18n";

import {
  createCampaignWhatsappDispatchFormSchema,
  TCampaignWhatsappDispatchFormData,
} from "../../campaigns.schema";
import type {
  TCampaign,
  TCampaignWhatsappDispatchPayload,
} from "../../campaigns.types";
import {
  campaignBodyParamsLinesFromCampaign,
  mapWhatsappDispatchFormToPayload,
} from "../../campaigns.utils";
import {
  buildTemplateComboboxValue,
  parseTemplateComboboxValue,
  useWhatsappMetaCascadeOptions,
} from "../../campaigns-whatsapp-form.hook";

export interface ICampaignWhatsappDispatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string | undefined;
  campaign: TCampaign | null | undefined;
  isPending: boolean;
  onValidSubmit: (payload: TCampaignWhatsappDispatchPayload) => void;
}

export const CampaignWhatsappDispatchDialog: React.FC<
  ICampaignWhatsappDispatchDialogProps
> = ({
  open,
  onOpenChange,
  organizationId,
  campaign,
  isPending,
  onValidSubmit,
}) => {
  const { t } = useI18n();
  const schema = React.useMemo(
    () => createCampaignWhatsappDispatchFormSchema(),
    []
  );

  const form = useForm<TCampaignWhatsappDispatchFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      whatsapp_meta_config_id: "",
      whatsapp_meta_organization_id: "",
      whatsapp_sender_phone_number_id: "",
      whatsapp_template_name: "",
      whatsapp_template_language: "",
      whatsapp_template_body_params_lines: "",
    },
  });

  const metaConfigId = form.watch("whatsapp_meta_config_id");
  const tplName = form.watch("whatsapp_template_name");
  const tplLang = form.watch("whatsapp_template_language");
  const templateComboboxValue =
    tplName && tplLang ? buildTemplateComboboxValue(tplName, tplLang) : undefined;

  const cascade = useWhatsappMetaCascadeOptions(
    metaConfigId || undefined,
    organizationId,
    open
  );

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const cfgId = campaign?.whatsapp_meta_config_id ?? "";
    const row = cfgId
      ? cascade.metaConfigs.find((c) => c.id === cfgId)
      : undefined;
    form.reset({
      whatsapp_meta_config_id: cfgId,
      whatsapp_meta_organization_id: row?.organization_id ?? "",
      whatsapp_sender_phone_number_id:
        campaign?.whatsapp_sender_phone_number_id ?? "",
      whatsapp_template_name: campaign?.whatsapp_template_name ?? "",
      whatsapp_template_language: campaign?.whatsapp_template_language ?? "",
      whatsapp_template_body_params_lines: campaign
        ? campaignBodyParamsLinesFromCampaign(campaign)
        : "",
    });
  }, [open, campaign, cascade.metaConfigs, form]);

  const handleSubmit = React.useCallback(
    (values: TCampaignWhatsappDispatchFormData) => {
      onValidSubmit(mapWhatsappDispatchFormToPayload(values));
    },
    [onValidSubmit]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("campaigns.details.dispatch.dialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("campaigns.details.dispatch.dialogDescription")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <div className="rounded-lg border border-border/80 bg-muted/20 px-4 py-3 space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name="whatsapp_meta_config_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Combobox
                          label={t("campaigns.form.whatsappMetaConfig")}
                          placeholder={
                            cascade.isMetaConfigsLoading
                              ? t("generic.loading")
                              : t("campaigns.form.whatsappMetaConfigPlaceholder")
                          }
                          options={cascade.metaConfigOptions}
                          value={field.value || undefined}
                          onChange={(value) => {
                            const next = value ?? "";
                            const orgRow = next
                              ? cascade.metaConfigs.find((c) => c.id === next)
                              : undefined;
                            form.setValue(
                              "whatsapp_meta_organization_id",
                              orgRow?.organization_id ?? ""
                            );
                            if (next !== field.value) {
                              form.setValue("whatsapp_sender_phone_number_id", "");
                              form.setValue("whatsapp_template_name", "");
                              form.setValue("whatsapp_template_language", "");
                              form.setValue(
                                "whatsapp_template_body_params_lines",
                                ""
                              );
                            }
                            field.onChange(next);
                          }}
                          disabled={cascade.isMetaConfigsLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="whatsapp_sender_phone_number_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Combobox
                          label={t("campaigns.form.whatsappSenderPhone")}
                          placeholder={
                            cascade.isPhonesLoading
                              ? t("generic.loading")
                              : t("campaigns.form.whatsappSenderPhonePlaceholder")
                          }
                          options={cascade.phoneOptions}
                          value={field.value || undefined}
                          onChange={(value) => field.onChange(value ?? "")}
                          disabled={
                            !metaConfigId || cascade.isPhonesLoading
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="whatsapp_template_name"
                  render={() => (
                    <FormItem>
                      <FormControl>
                        <Combobox
                          label={t("campaigns.form.whatsappTemplate")}
                          placeholder={
                            cascade.isTemplatesLoading
                              ? t("generic.loading")
                              : t("campaigns.form.whatsappTemplatePlaceholder")
                          }
                          options={cascade.templateOptions}
                          value={templateComboboxValue}
                          onChange={(value) => {
                            if (!value) {
                              form.setValue("whatsapp_template_name", "");
                              form.setValue("whatsapp_template_language", "");
                              return;
                            }
                            const parsed = parseTemplateComboboxValue(value);
                            if (parsed) {
                              form.setValue("whatsapp_template_name", parsed.name);
                              form.setValue(
                                "whatsapp_template_language",
                                parsed.language
                              );
                            }
                          }}
                          disabled={!metaConfigId || cascade.isTemplatesLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="whatsapp_template_body_params_lines"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingLabelTextarea
                          label={t("campaigns.form.whatsappBodyParams")}
                          placeholder=" "
                          className="min-h-[88px]"
                          {...field}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        {t("campaigns.details.dispatch.bodyParamsHint")}
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t("generic.cancel")}
              </Button>
              <Button type="submit" className="w-full sm:w-auto" disabled={isPending}>
                {isPending
                  ? t("generic.loading")
                  : t("campaigns.details.dispatch.dialogSubmit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
